// Captcha Verification Service
// This service handles reCAPTCHA token verification

import RECAPTCHA_CONFIG from '../config/captchaConfig';

/**
 * Verify reCAPTCHA token via Azure Function (server-side)
 * @param {string} token - The captcha token to verify
 * @param {string} remoteIp - The user's IP address (optional)
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const verifyCaptchaToken = async (token, remoteIp = null) => {
  try {
    if (!token) {
      return { success: false, error: 'No captcha token provided' };
    }

    // For development with test keys, we can skip actual verification
    if (RECAPTCHA_CONFIG.siteKey === '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI') {
      console.log('Development mode: Skipping captcha verification');
      return { success: true };
    }

    // Use Azure Function for server-side verification
    const response = await fetch('/api/verify-captcha', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        token: token,
        remoteIp: remoteIp
      })
    });

    const result = await response.json();

    if (result.success) {
      return { success: true };
    } else {
      return { 
        success: false, 
        error: result.error || 'Captcha verification failed' 
      };
    }
  } catch (error) {
    console.error('Captcha verification error:', error);
    return { 
      success: false, 
      error: `Captcha verification failed: ${error.message}` 
    };
  }
};

/**
 * Get user's IP address (for server-side verification)
 * This is a client-side approximation - in production, you'd get this from your server
 * @returns {string}
 */
export const getUserIP = () => {
  // This is a simplified approach - in production, you'd get the real IP from your server
  return '127.0.0.1';
};

/**
 * Validate captcha token format
 * @param {string} token - The captcha token to validate
 * @returns {boolean}
 */
export const isValidCaptchaToken = (token) => {
  if (!token || typeof token !== 'string') {
    return false;
  }
  
  // Basic token format validation
  return token.length > 10 && /^[A-Za-z0-9_-]+$/.test(token);
};
